// Since XP-economy Phase B (migration 0192) these are HYDRATION TARGETS:
// award_social_xp() reads the same console-managed tables server-side
// (xp_actions / xp_config / xp_titles / xp_rates), and the app overwrites
// the values below on boot so display always matches what the server pays.
// The literals are the offline/fallback defaults.
export const SOCIAL_XP_VALUES: Record<string, Record<string, number>> = {
  micro: {
    like: 2,
    save: 2,
    view: 1,
    react: 3,
  },
  communication: {
    comment: 5,
    reply: 5,
    message: 3,
    storyReact: 3,
    encourage: 5,
  },
  contribution: {
    post: 15,
    story: 10,
    share: 10,
    copy: 5,
    upload: 10,
  },
  collaborative: {
    guildPost: 15,
    questJoin: 20,
    eventRsvp: 10,
    projectContribute: 25,
    libraryAdd: 20,
  },
  leadership: {
    guildCreate: 100,
    eventHost: 50,
    mentor: 25,
    pactCreate: 20,
    questCreate: 30,
    moderate: 15,
  },
};

export const SOCIAL_XP_MULTIPLIERS: Record<string, number> = {
  streak7Days: 1.10,
  guildEvent: 1.20,
  qualityPost: 1.30,
  leadership: 1.25,
  mentorship: 1.20,
};

export interface SocialTitle {
  minLevel: number;
  maxLevel: number;
  title: string;
  description: string;
}

export const SOCIAL_TITLES: SocialTitle[] = [
  { minLevel: 1, maxLevel: 5, title: 'Observer', description: "You're starting to engage and connect." },
  { minLevel: 6, maxLevel: 10, title: 'Participant', description: 'You contribute regularly to conversations.' },
  { minLevel: 11, maxLevel: 20, title: 'Connector', description: 'You help others find value and people.' },
  { minLevel: 21, maxLevel: 30, title: 'Collaborator', description: 'You co-create and participate in group growth.' },
  { minLevel: 31, maxLevel: 40, title: 'Mentor', description: 'You guide and inspire others.' },
  { minLevel: 41, maxLevel: Infinity, title: 'Catalyst', description: 'You amplify growth for communities.' },
];

export let SOCIAL_XP_PER_LEVEL = 1000;

export function hydrateSocialXpConfig(
  actions: Array<{ key: string; base_xp: number; category: string }>,
  titles: Array<{ min_level: number; name: string; description: string }>,
  rates: Array<{ kind: string; key: string; multiplier: number }>,
  xpPerLevel: number | null,
): void {
  // Action values — keys are unique across the nested categories, so update
  // whichever bucket holds each key. Console-added actions the client doesn't
  // know stay server-only (the RPC still pays them).
  for (const a of actions) {
    if (a.category !== 'social' || !Number.isFinite(a.base_xp)) continue;
    for (const bucket of Object.values(SOCIAL_XP_VALUES)) {
      if (a.key in bucket) {
        bucket[a.key] = a.base_xp;
        break;
      }
    }
  }

  const ladder = titles
    .filter((t) => Number.isFinite(t.min_level))
    .sort((a, b) => a.min_level - b.min_level);
  if (ladder.length > 0) {
    SOCIAL_TITLES.length = 0;
    SOCIAL_TITLES.push(...ladder.map((t, i) => ({
      minLevel: t.min_level,
      maxLevel: i + 1 < ladder.length ? ladder[i + 1].min_level - 1 : Infinity,
      title: t.name,
      description: t.description,
    })));
  }

  const streak = rates.find((r) => r.kind === 'social_multiplier' && r.key === 'streak_7d');
  if (streak && Number.isFinite(streak.multiplier)) SOCIAL_XP_MULTIPLIERS.streak7Days = streak.multiplier;
  const guild = rates.find((r) => r.kind === 'social_multiplier' && r.key === 'guild_event');
  if (guild && Number.isFinite(guild.multiplier)) SOCIAL_XP_MULTIPLIERS.guildEvent = guild.multiplier;

  if (xpPerLevel && Number.isFinite(xpPerLevel) && xpPerLevel > 0) {
    SOCIAL_XP_PER_LEVEL = xpPerLevel;
  }
}

export function getSocialTitleForLevel(level: number): { title: string; description: string } {
  const titleData = SOCIAL_TITLES.find(
    t => level >= t.minLevel && level <= t.maxLevel
  );
  return titleData || SOCIAL_TITLES[0];
}

export function getXPForAction(
  category: keyof typeof SOCIAL_XP_VALUES,
  action: string
): number {
  const categoryValues = SOCIAL_XP_VALUES[category];
  if (!categoryValues) return 0;
  return (categoryValues as any)[action] || 0;
}

export function calculateSocialLevel(xp: number): number {
  return Math.floor(xp / SOCIAL_XP_PER_LEVEL) + 1;
}

export function getXPForNextLevel(currentXP: number): number {
  const currentLevel = calculateSocialLevel(currentXP);
  return currentLevel * SOCIAL_XP_PER_LEVEL;
}

export function getXPProgressInCurrentLevel(currentXP: number): {
  current: number;
  required: number;
  percentage: number;
} {
  const levelStartXP = (calculateSocialLevel(currentXP) - 1) * SOCIAL_XP_PER_LEVEL;
  const xpInCurrentLevel = currentXP - levelStartXP;
  const xpRequiredForLevel = SOCIAL_XP_PER_LEVEL;

  return {
    current: xpInCurrentLevel,
    required: xpRequiredForLevel,
    percentage: Math.min(100, (xpInCurrentLevel / xpRequiredForLevel) * 100),
  };
}
